import React, {useState} from 'react';
import {startRegistration, startAuthentication} from '@simplewebauthn/browser';
import {Form, Button, Box, Content} from 'react-bulma-components';
import * as WebAuthnService from '../services/webauthn';

declare module '@simplewebauthn/typescript-types' {
  interface AuthenticationExtensionsClientInputs {
    largeBlob?: {
      write?: BufferSource;
    };
  }
}

const WebAuthnPage: React.FC = () => {
  const [username, setUsername] = useState('');

  const [transaction, setTransaction] = useState('');

  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (message: string) => {
    setLogs((prevLogs) => [
      ...prevLogs,

      `${new Date().toISOString()}: ${message}`,
    ]);
  };

  const handleRegister = async () => {
    if (!username) {
      addLog('Error: Username is required');

      return;
    }

    try {
      // Get registration options from server
      const optionsJSON = await WebAuthnService.requestRegistrationOptions(
        username
      );
      addLog('Received registration options from server');
      addLog(JSON.stringify(optionsJSON, null, 2));

      // Start the registration process
      optionsJSON.timeout = 600_000; // Set a longer timeout for testing
      const regResult = await startRegistration({optionsJSON});
      addLog('Registration response generated');
      addLog(JSON.stringify(regResult, null, 2));

      // Verify the registration with the server
      const verifyResponse = await WebAuthnService.verifyRegistration(
        username,
        regResult
      );
      if (verifyResponse.verified) {
        addLog('Registration verified successfully!');
      } else {
        addLog('Registration verification failed');
      }
    } catch (error) {
      addLog(
        `Registration error: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  };

  const handleLogin = async () => {
    if (!username) {
      addLog('Error: Username is required');
      return;
    }

    try {
      // Get authentication options from server
      const optionsJSON = await WebAuthnService.requestAuthenticationOptions(
        username
      );
      addLog('Received authentication options from server');
      addLog(JSON.stringify(optionsJSON, null, 2));

      // Start the authentication process
      optionsJSON.timeout = 600_000; // Set a longer timeout for testing
      const authResult = await startAuthentication({optionsJSON});
      addLog('Authentication response generated');
      addLog(JSON.stringify(authResult, null, 2));

      // Verify the authentication with the server
      const verifyResponse = await WebAuthnService.verifyAuthentication(
        username,
        authResult
      );
      if (verifyResponse.verified) {
        addLog('Authentication verified successfully!');
      } else {
        addLog('Authentication verification failed');
      }
    } catch (error) {
      addLog(
        `Authentication error: ${
          error instanceof Error ? error.message : JSON.stringify(error)
        }`
      );
    }
  };

  const strToBytes = (str: string): Uint8Array =>
    new Uint8Array(str.split('').map((c) => c.charCodeAt(0)));

  const handleConfirmWithCredentialID = async () => {
    if (!username || !transaction) {
      addLog('Error: Both username and transaction are required');
      return;
    }

    try {
      const optionsJSON =
        await WebAuthnService.requestCredentialIDAuthenticationOptions(
          username,
          transaction
        );
      addLog(
        'Received method 1 (credentialID) authentication options from server'
      );
      addLog(JSON.stringify(optionsJSON, null, 2));

      optionsJSON.timeout = 600_000;
      const authResult = await startAuthentication({optionsJSON});
      addLog('Authentication response generated');
      addLog(JSON.stringify(authResult, null, 2));

      const verifyResponse = await WebAuthnService.verifyTransaction(
        username,
        authResult
      );
      addLog(
        `Received transaction verification response: ${JSON.stringify(
          verifyResponse
        )}`
      );
      if (verifyResponse.verified && verifyResponse.signatureValid) {
        addLog('Transaction confirmed successfully!');
      } else if (verifyResponse.verified && !verifyResponse.signatureValid) {
        addLog('Transaction confirmed, but signature is invalid');
      } else {
        addLog('Transaction confirmation failed');
      }
    } catch (error) {
      addLog(
        `Transaction confirmation error: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  };

  const handleConfirmWithLargeBlob = async () => {
    if (!username || !transaction) {
      addLog('Error: Both username and transaction are required');
      return;
    }

    try {
      const optionsJSON =
        await WebAuthnService.requestLargeBlobAuthenticationOptions(
          username,
          transaction
        );
      addLog(
        'Received method 2 (largeBlob) authentication options from server'
      );
      addLog(JSON.stringify(optionsJSON, null, 2));

      // TODO: This should be done on the backend
      const blob = strToBytes(transaction);
      optionsJSON.extensions = {
        largeBlob: {
          write: blob,
        },
      } as any;
      addLog('Added largeBlob extension to options');
      addLog('Updated options with largeBlob extension:');
      addLog(JSON.stringify(optionsJSON, null, 2));

      optionsJSON.timeout = 600_000;
      const authResult = await startAuthentication({optionsJSON});
      addLog('Authentication response generated');

      const verifyResponse = await WebAuthnService.verifyTransactionLargeBlob(
        username,
        authResult
      );
      addLog(
        `Received transaction verification response: ${JSON.stringify(
          verifyResponse,
          null,
          2
        )}`
      );
      if (verifyResponse.verified && verifyResponse.signatureValid) {
        addLog('Transaction confirmed successfully!');
      } else if (verifyResponse.verified && !verifyResponse.signatureValid) {
        addLog('Transaction confirmed, but signature is invalid');
      } else {
        addLog('Transaction confirmation failed');
      }
    } catch (error) {
      addLog(
        `Transaction confirmation error: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  };

  const handleConfirmWithSecondCredential = async () => {
    if (!username || !transaction) {
      addLog('Error: Both username and transaction are required');
      return;
    }

    try {
      const optionsJSON =
        await WebAuthnService.requestSecondCredentialAuthenticationOptions(
          username,
          transaction
        );
      addLog(
        `Received authentication options from server: ${JSON.stringify(
          optionsJSON,
          null,
          2
        )}`
      );

      optionsJSON.timeout = 600_000;
      const authResult = await startAuthentication({optionsJSON});
      addLog(
        `Authentication response generated: ${JSON.stringify(
          authResult,
          null,
          2
        )}`
      );

      const verifyResponse =
        await WebAuthnService.verifyTransactionSecondCredential(
          username,
          authResult
        );
      addLog(
        `Received transaction verification response: ${JSON.stringify(
          verifyResponse,
          null,
          2
        )}`
      );
      if (verifyResponse.verified && verifyResponse.signatureValid) {
        addLog('Transaction confirmed successfully!');
      } else if (verifyResponse.verified && !verifyResponse.signatureValid) {
        addLog('Transaction confirmed, but signature is invalid');
      } else {
        addLog('Transaction confirmation failed');
      }
    } catch (error) {
      addLog(`Transaction confirmation error: ${JSON.stringify(error)}$`);
    }
  };

  return (
    <Box style={{maxWidth: '600px', margin: '2rem auto'}}>
      <Form.Field>
        <Form.Label>Username</Form.Label>

        <Form.Control>
          <Form.Input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter username"
          />
        </Form.Control>
      </Form.Field>
      <Form.Field kind="group">
        <Form.Control>
          <Button color="primary" onClick={handleRegister}>
            Register
          </Button>
        </Form.Control>

        <Form.Control>
          <Button color="info" onClick={handleLogin}>
            Login
          </Button>
        </Form.Control>
      </Form.Field>
      <Form.Field>
        <Form.Label>Transaction</Form.Label>

        <Form.Control>
          <Form.Input
            type="text"
            value={transaction}
            onChange={(e) => setTransaction(e.target.value)}
            placeholder="Enter transaction text"
          />
        </Form.Control>
      </Form.Field>
      <Form.Field>
        <Form.Control>
          <Button color="success" onClick={handleConfirmWithCredentialID}>
            Method 1: Confirm with credentialID
          </Button>
        </Form.Control>
      </Form.Field>
      <Form.Field>
        <Form.Control>
          <Button color="success" onClick={handleConfirmWithLargeBlob}>
            Method 2: Confirm with largeBlob
          </Button>
        </Form.Control>
      </Form.Field>
      <Form.Field>
        <Form.Control>
          <Button color="success" onClick={handleConfirmWithSecondCredential}>
            Method 3: Confirm with second credentialID
          </Button>
        </Form.Control>
      </Form.Field>{' '}
      <Content>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
          }}
        >
          <Form.Label>Logs</Form.Label>
          <div style={{display: 'flex', gap: '0.5rem'}}>
            <Button
              size="small"
              onClick={() => navigator.clipboard.writeText(logs.join('\n'))}
            >
              Copy
            </Button>
            <Button size="small" color="light" onClick={() => setLogs([])}>
              Clear logs
            </Button>
          </div>
        </div>

        <Box
          style={{
            backgroundColor: '#f5f5f5',
            padding: '1rem',
            maxHeight: '800px',
            overflowY: 'auto',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
          }}
        >
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </Box>
      </Content>
    </Box>
  );
};

export default WebAuthnPage;
